import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Instagram, Facebook, Music2, Store, Hourglass, Bell, Gem } from "lucide-react";
import Reveal from "@/components/Reveal";
import DropAlerts from "@/components/DropAlerts";
import Marquee from "@/components/Marquee";

const drops = [
  { title: "Curated Singles", body: "Hand-picked high-value cards from vault acquisitions — graded grails, vintage holos, and Reserved List staples." },
  { title: "Sealed Treasures", body: "Booster boxes, ETBs, and sealed cases sourced from collections across the country, stored vault-cool and mint." },
  { title: "Collection Highlights", body: "Before big collections are broken up, the best finds are previewed to our community on Instagram and TikTok." },
];

const socials = [
  { Icon: Instagram, label: "Instagram", handle: "@AardvarksVault", href: "https://instagram.com/AardvarksVault", note: "Live sales & drop previews", id: "marketplace-social-instagram" },
  { Icon: Facebook, label: "Facebook", handle: "Aardvarks Vault", href: "https://facebook.com/AardvarksVault", note: "Community & restock posts", id: "marketplace-social-facebook" },
  { Icon: Music2, label: "TikTok", handle: "@AardvarksVault", href: "https://tiktok.com/@AardvarksVault", note: "Pack openings & vault tours", id: "marketplace-social-tiktok" },
];

const Marketplace = () => {
  useEffect(() => {
    document.title = "Marketplace — Aardvarks Vault";
  }, []);

  return (
    <div data-testid="marketplace-page">
      <section className="px-5 pb-16 pt-40 sm:px-8 lg:pt-48" data-testid="marketplace-hero">
        <div className="mx-auto max-w-4xl text-center">
          <motion.img
            src="/brand/mascot-banner.png"
            alt="Aardvarks Vault mascot presenting trading card treasures"
            className="mx-auto mb-8 w-64 animate-floaty sm:w-80"
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            data-testid="marketplace-hero-mascot"
          />
          <p className="eyebrow">Marketplace</p>
          <h1 className="mt-5 font-serif text-4xl font-bold leading-[1.08] tracking-tight text-cream sm:text-5xl lg:text-6xl" data-testid="marketplace-hero-title">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}>
                Vault treasures &
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span className="block text-gold" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}>
                direct drops.
              </motion.span>
            </span>
          </h1>
          <motion.p
            className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-cream/80"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            The cards we buy don't sit still. Follow the Vault to catch curated singles, sealed product, and
            collection highlights the moment they surface.
          </motion.p>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8" data-testid="marketplace-drops-section">
        <Reveal>
          <p className="eyebrow flex items-center gap-2"><Gem size={14} /> What Surfaces Here</p>
          <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
            Fresh from the vault floor
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 md:grid-cols-3">
          {drops.map((d, i) => (
            <Reveal key={d.title} delay={i * 0.12}>
              <div className="card-frame foil-sheen h-full p-8" data-testid={`drop-card-${i}`}>
                <span className="font-display text-3xl font-black text-mint/40">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-4 font-serif text-lg font-semibold text-cream">{d.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-cream/70">{d.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-gold/10 bg-ink/40 py-24" data-testid="marketplace-social-section">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">Where the Drops Happen Now</p>
            <h2 className="mt-4 font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl lg:text-4xl">
              Follow the hunt on social
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {socials.map(({ Icon, label, handle, href, note, id }, i) => (
              <Reveal key={label} delay={i * 0.12}>
                <a
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={id}
                  className="card-frame foil-sheen group flex h-full flex-col p-8 transition-transform duration-300 hover:-translate-y-1"
                >
                  <Icon size={30} className="text-mint transition-colors group-hover:text-gold" />
                  <h3 className="mt-5 font-serif text-lg font-semibold text-cream">{label}</h3>
                  <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{handle}</p>
                  <p className="mt-3 text-sm leading-relaxed text-cream/70">{note}</p>
                </a>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-14 flex flex-col items-start gap-5 rounded-2xl border border-dashed border-gold/35 bg-navy/60 p-8 sm:flex-row sm:items-center" data-testid="storefronts-notice">
              <Hourglass className="shrink-0 text-gold" size={30} />
              <div>
                <h3 className="font-serif text-lg font-semibold text-cream">eBay & TCGplayer storefronts — opening soon</h3>
                <p className="mt-2 max-w-2xl text-sm leading-relaxed text-cream/70">
                  Our dedicated eBay and TCGplayer storefronts are being stocked and will be linked here the moment
                  they launch. Until then, the fastest way to catch new inventory is our social channels above.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* DROP ALERTS */}
      <section className="mx-auto max-w-3xl px-5 py-24 sm:px-8" data-testid="marketplace-alerts-section">
        <Reveal>
          <DropAlerts />
        </Reveal>
      </section>

      <section className="mx-auto max-w-4xl px-5 py-24 text-center sm:px-8" data-testid="marketplace-cta-section">
        <Reveal>
          <Store className="mx-auto text-mint" size={34} />
          <h2 className="mt-5 font-serif text-2xl font-bold tracking-tight text-cream sm:text-3xl">
            Have cards the marketplace should see?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-cream/70 sm:text-base">
            Sell your collection to the Vault and your best pulls could headline the next drop — while you head
            home with the payout.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/sell" state={{ scrollToForm: true }} className="btn-gold" data-testid="marketplace-sell-cta">
              Sell to the Vault
            </Link>
            <a href="https://instagram.com/AardvarksVault" target="_blank" rel="noopener noreferrer" className="btn-ghost" data-testid="marketplace-follow-cta">
              <Bell size={15} /> Follow for Drops
            </a>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default Marketplace;
